import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { wordsAndRooms } from "../../data/word/WordsAndRooms";
import { WordsAndRooms } from "../../data/word/WordsAndRooms";
import { FaLocationDot } from "react-icons/fa6";
import { MdBedroomChild } from "react-icons/md";
import { HiCurrencyDollar } from "react-icons/hi2";
import ImageGallery from "../../components/wardImageGallery/ImageGalleryComponent";
import { decryptId } from "../../components/encryption/encryption";
import BreadcrumbsComponent from "../../components/common/BreadcrumbsComponent";

const WordAndRoomSinglePage = () => {
  const { encryptedId } = useParams<{ encryptedId?: string }>();
  let roomId;
  if (encryptedId) {
    roomId = decryptId(encryptedId);
  }

  const [activeSection, setActiveSection] = useState<string | null>(null);
  const sectionRefs = useRef<{ [key: string]: HTMLElement | null }>({});

  if (!roomId) return <div>Error: Room ID not provided</div>;
  const roomDetails = wordsAndRooms.find(
    (room: WordsAndRooms) => room.id === roomId
  );
  if (!roomDetails) return <div>Room not found</div>;

  const sections = [
    { id: "headsec", name: "Room Details" },
    { id: "gallery", name: "Overview" },
    { id: "amenities", name: "Room Amenities" },
    { id: "bath", name: "Bath and Toiletries" },
    { id: "food", name: "Food and Beverage" },
    { id: "services", name: "Complimentary Services" },
    { id: "additionalPaidServices", name: "Additional Paid Services" },
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      { threshold: 0.6, rootMargin: "0px 0px -20% 0px" }
    );

    sections.forEach((section) => {
      const element = sectionRefs.current[section.id];
      if (element) observer.observe(element);
    });

    return () => {
      sections.forEach((section) => {
        const element = sectionRefs.current[section.id];
        if (element) observer.unobserve(element);
      });
    };
  }, []);

  const scrollToSection = (sectionId: string) => {
    const element = sectionRefs.current[sectionId];
    if (element) {
      element.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };

const BreadCrumbsPath = [
  {
    id: 1,  
    pathName: "Service",
    pathLink: "/service",
  },
  {
    id: 2, 
    pathName: "Ch-ward",
    pathLink: "/service/ch-ward",
  },
  {
    id: 3, 
    pathName: "ward-details",
    pathLink: `/service/ch-ward/ward-details/${encryptedId}`,
  },
];

  return (
    <>
    <BreadcrumbsComponent breadCrumbsPath={BreadCrumbsPath} />
    <div className="container mx-auto lg:px-4 lg:mt-6 mt-20">
      <div className="flex flex-row ">
        {/* Left Content */}
        <div className="lg:w-9/12 w-full">
          <div className="lg:px-8 px-2">
            {/* Room Section */}
            <section
              id="headsec"
              ref={(el) => {
                sectionRefs.current["headsec"] = el;
              }}
              className="mb-6"
            >
              <img
                src={roomDetails.mainImage}
                alt={roomDetails.roomType}
                loading="lazy"
                className="w-full h-auto rounded-sm shadow-md max-h-[500px]"
              />
              <div className="bg-green-100/50 py-2 px-4 mt-2">
                <h1 className="lg:text-4xl text-xl font-bold mb-2 mt-3 lg:Blinker-Bold Blinker-SemiBold leading-tight">
                  {roomDetails.roomType}
                </h1>
                <div className="flex flex-row gap-2 items-center Blinker-Regular text-lg">
                  <FaLocationDot color="green" />
                  {roomDetails.location}
                </div>
                <div className="flex flex-row gap-2 items-center Blinker-Regular text-lg">
                  <MdBedroomChild color="green" />
                  <span className="Blinker-SemiBold">Room Size :</span>
                  {roomDetails.roomSize}
                </div>
                <div className="flex flex-row gap-2 items-center Blinker-Regular text-lg">
                  <HiCurrencyDollar color="green" />
                  <span className="Blinker-SemiBold">From Price:</span>
                  {roomDetails.fromPrice}
                </div>

                <div className="flex flex-row gap-2  Blinker-Regular text-sm mt-6">
                  <span className="flex w-1">*</span>{" "}
                  <p className=" w-11/12">
                    All information contained here is indicative and accurate as
                    of 1 June 2025.
                  </p>
                </div>
              </div>
            </section>
            {/* image gallery */}

            <section
              id="gallery"
              ref={(el) => {
                sectionRefs.current["gallery"] = el;
              }}
              className="container mx-auto lg:px-9 py-8 mb-6"
            >
              <ImageGallery images={roomDetails.image} />
            </section>
            {/* Room Amenities Section */}
            <section
              id="amenities"
              ref={(el) => {
                sectionRefs.current["amenities"] = el;
              }}
              className="mb-6"
            >
              <h1 className="Blinker-SemiBold text-3xl">Room Amenities</h1>
              <div className="grid lg:grid-cols-2 gap-2 p-2">
                {roomDetails.roomAmenities.map((amenity, index) => {
                  const Icon = amenity.icon;
                  return (
                    <div
                      className="flex flex-row gap-4 items-center bg-green-100/50 py-3 px-5"
                      key={index}
                    >
                      <div>
                        <Icon className="text-green-900 h-7 w-7" />
                      </div>
                      <p className="Blinker-Regular text-lg text-gray-700">
                        {amenity.name}
                      </p>
                    </div>
                  );
                })}
              </div>
            </section>

            {/* Bath And Toiletries Section */}
            <section
              id="bath"
              ref={(el) => {
                sectionRefs.current["bath"] = el;
              }}
              className="mb-6"
            >
              <h1 className="Blinker-SemiBold text-3xl">Bath And Toiletries</h1>
              <div className="grid lg:grid-cols-2 gap-2 p-2">
                {roomDetails.bathAndToiletries.map((amenity, index) => {
                  const Icon = amenity.icon;
                  return (
                    <div
                      className="flex flex-row gap-4 items-center bg-green-100/50 py-3 px-5"
                      key={index}
                    >
                      <div>
                        <Icon className="text-green-900 h-7 w-7" />
                      </div>
                      <p className="Blinker-Regular text-lg text-gray-700">
                        {amenity.name}
                      </p>
                    </div>
                  );
                })}
              </div>
            </section>

            {/* Food And Beverage Section */}
            <section
              id="food"
              ref={(el) => {
                sectionRefs.current["food"] = el;
              }}
              className="mb-6"
            >
              <h1 className="Blinker-SemiBold text-3xl">Food And Beverage</h1>
              <div className="grid lg:grid-cols-2 gap-2 p-2">
                {roomDetails.foodAndBeverage.map((amenity, index) => {
                  const Icon = amenity.icon;
                  return (
                    <div
                      className="flex flex-row gap-4 items-center bg-green-100/50 py-3 px-5"
                      key={index}
                    >
                      <div>
                        <Icon className="text-green-900 h-7 w-7" />
                      </div>
                      <p className="Blinker-Regular text-lg text-gray-700">
                        {amenity.name}
                      </p>
                    </div>
                  );
                })}
              </div>
            </section>

            {/* Complimentary Services Section */}
            <section
              id="services"
              ref={(el) => {
                sectionRefs.current["services"] = el;
              }}
              className="mb-6"
            >
              <h1 className="Blinker-SemiBold text-3xl">
                Complimentary Services
              </h1>
              <div className="grid lg:grid-cols-2 gap-2 p-2">
                {roomDetails.complimentaryServices.map((amenity, index) => {
                  const Icon = amenity.icon;
                  return (
                    <div
                      className="flex flex-row gap-4 items-center bg-green-100/50 py-3 px-5"
                      key={index}
                    >
                      <div>
                        <Icon className="text-green-900 h-7 w-7" />
                      </div>
                      <p className="Blinker-Regular text-lg text-gray-700">
                        {amenity.name}
                      </p>
                    </div>
                  );
                })}
              </div>
            </section>

            {/* Additional Paid Services Section */}
            <section
              id="additionalPaidServices"
              ref={(el) => {
                sectionRefs.current["additionalPaidServices"] = el;
              }}
              className="mb-6"
            >
              <h1 className="Blinker-SemiBold text-3xl">
                Additional Paid Services
              </h1>
              <div className="grid lg:grid-cols-2 gap-2 p-2">
                {roomDetails.additionalPaidServices.map((amenity, index) => {
                  const Icon = amenity.icon;
                  return (
                    <div
                      className="flex flex-row gap-4 items-center bg-green-100/50 py-3 px-5"
                      key={index}
                    >
                      <div>
                        <Icon className="text-green-900 h-7 w-7" />
                      </div>
                      <p className="Blinker-Regular text-lg text-gray-700">
                        {amenity.name}
                      </p>
                    </div>
                  );
                })}
              </div>
            </section>

            {/* Important Notes Section */}
            <section>
              <div className="p-4 bg-yellow-50 rounded-lg border border-red-200 mt-4 mb-4">
                <h1 className="Blinker-SemiBold text-3xl mb-3 text-red-400">
                  Important Note!
                </h1>
                <ul className="list-disc pl-5 space-y-2 text-gray-600">
                  {roomDetails.importantNotes.map((item, index) => (
                    <li key={index} className="Blinker-Regular leading-tight">
                      {item.description}
                    </li>
                  ))}
                </ul>
              </div>
            </section>

            <div className="mt-3 bg-white rounded-lg shadow-md overflow-hidden border border-gray-200 mb-4 lg:hidden block">
              {/* Card Header */}
              <div className="bg-green-600 p-4">
                <h2 className="text-xl font-bold text-white Blinker-SemiBold flex items-center gap-2">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z"
                      clipRule="evenodd"
                    />
                  </svg>
                  Need Help? Contact Us
                </h2>
                <p className="text-green-100 Blinker-Regular mt-1 text-sm">
                  For general enquiries and appointments
                </p>
              </div>

              {/* Card Body */}
              <div className="p-5 space-y-4">
                {/* WhatsApp Contact */}
                <div className="flex items-start gap-3">
                  <div className="bg-green-100 p-2 rounded-full">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5 text-green-600"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-medium Blinker-SemiBold text-gray-800">
                      WhatsApp Appointments
                    </h3>
                    <p className="text-gray-600 Blinker-Regular">
                      <a
                        href="https://wa.me/94705509888"
                        className="text-blue-600 hover:underline"
                      >
                        +94 70 550 9888
                      </a>
                      <br />
                      <span className="text-sm text-gray-500">
                        (Ceylon Hospital Yakkala)
                      </span>
                    </p>
                  </div>
                </div>

                {/* Hospital Hotline */}
                <div className="flex items-start gap-3">
                  <div className="bg-blue-100 p-2 rounded-full">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5 text-blue-600"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-medium Blinker-SemiBold text-gray-800">
                      Hospital Hotline
                    </h3>
                    <p className="text-gray-600 Blinker-Regular">
                      <a
                        href="tel:+94712509898"
                        className="text-blue-600 hover:underline"
                      >
                        +94 71 250 9898
                      </a>
                      <br />
                      <span className="text-sm text-gray-500">
                        (Ceylon Hospital Yakkala)
                      </span>
                    </p>
                  </div>
                </div>

                {/* Additional Info */}
                <div className="pt-2 border-t border-gray-100">
                  <p className="text-sm text-gray-500 Blinker-Regular">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-4 w-4 inline mr-1"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z"
                        clipRule="evenodd"
                      />
                    </svg>
                    Operating Hours: 24/7 Services
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Side Navigation */}
        <div className="w-3/12 px-1 lg:block hidden sticky top-20 h-fit">
          <div className="space-y-3 Blinker-Regular ">
            <h1 className="text-lg font-bold mb-2  Blinker-SemiBold bg-green-500 p-2 rounded-t-md text-white">
              {roomDetails.roomType}
            </h1>

            {sections.map((section) => (
              <button
                key={section.id}
                onClick={() => scrollToSection(section.id)}
                className={`block w-full text-left px-1 rounded transition-colors ${
                  activeSection === section.id
                    ? " text-green-800 Blinker-SemiBold text-lg "
                    : "hover:text-green-800"
                }`}
              >
                {section.name}
              </button>
            ))}
            <div className=" border-b mt-4" />

            <div className="mt-3 bg-white rounded-lg shadow-md overflow-hidden border border-gray-200">
              {/* Card Header */}
              <div className="bg-green-600 p-4">
                <h2 className="text-xl font-bold text-white Blinker-SemiBold flex items-center gap-2">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z"
                      clipRule="evenodd"
                    />
                  </svg>
                  Need Help? Contact Us
                </h2>
                <p className="text-green-100 Blinker-Regular mt-1 text-sm">
                  For general enquiries and appointments
                </p>
              </div>

              {/* Card Body */}
              <div className="p-5 space-y-4">
                {/* WhatsApp Contact */}
                <div className="flex items-start gap-3">
                  <div className="bg-green-100 p-2 rounded-full">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5 text-green-600"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-medium Blinker-SemiBold text-gray-800">
                      WhatsApp Appointments
                    </h3>
                    <p className="text-gray-600 Blinker-Regular">
                      <a
                        href="https://wa.me/94705509888"
                        className="text-blue-600 hover:underline"
                      >
                        +94 70 550 9888
                      </a>
                      <br />
                      <span className="text-sm text-gray-500">
                        (Ceylon Hospital Yakkala)
                      </span>
                    </p>
                  </div>
                </div>

                {/* Hospital Hotline */}
                <div className="flex items-start gap-3">
                  <div className="bg-blue-100 p-2 rounded-full">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5 text-blue-600"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-medium Blinker-SemiBold text-gray-800">
                      Hospital Hotline
                    </h3>
                    <p className="text-gray-600 Blinker-Regular">
                      <a
                        href="tel:+94712509898"
                        className="text-blue-600 hover:underline"
                      >
                        +94 71 250 9898
                      </a>
                      <br />
                      <span className="text-sm text-gray-500">
                        (Ceylon Hospital Yakkala)
                      </span>
                    </p>
                  </div>
                </div>

                {/* Additional Info */}
                <div className="pt-2 border-t border-gray-100">
                  <p className="text-sm text-gray-500 Blinker-Regular">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-4 w-4 inline mr-1"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z"
                        clipRule="evenodd"
                      />
                    </svg>
                    Operating Hours: 24/7 Services
                  </p>
                </div>
              </div>
            </div>

            <div className=" border-b mt-4" />
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default WordAndRoomSinglePage;
